/**
 * The right pane of the configure screen: one sample document in full (an
 * observation raw, as Langfuse returns it), to see which paths exist and to
 * click one into a mapping row, and the Test button.
 */

import { Box } from "@/components/ui/box";
import { JsonView } from "@/components/ui/json-view";
import { QueryState } from "@/components/ui/states";
import { hasSample, useEvaluatorSample } from "./hooks";
import { SamplePicker } from "./sample-picker";
import { mappingPath } from "./sample-paths";
import { TestPanel } from "./test-panel";
import type { EvaluatorCreate } from "./types";
import type { SampleChoice } from "./use-sample-choice";

export function SamplePane({
  s,
  body,
  problem,
  focused,
  onPick,
}: {
  s: SampleChoice;
  body: EvaluatorCreate;
  problem: string | null;
  focused: string | null;
  onPick: (path: string) => void;
}) {
  const sample = useEvaluatorSample(s.ref);
  const chosen = hasSample(s.ref);
  const doc = sample.data;
  const hasOutput = doc?.output !== undefined && doc?.output !== null;
  const raw = s.source === "trace";

  return (
    <Box className="flex flex-col gap-4 p-5" label="Sample data">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-sm font-semibold">Sample data</h2>
        <span className="text-xs text-muted">
          {focused
            ? `Click a field, or Use on its row, to map it to {{${focused}}}.`
            : "Pick a mapping row, then click a field here to fill it."}
        </span>
      </div>
      <SamplePicker s={s} />
      {chosen && (
        <QueryState query={sample} what="the sample">
          {doc && (
            <JsonView
              value={doc}
              label={raw ? "Observation, raw from the source" : "Sample document"}
              onPick={raw ? (p) => onPick(mappingPath(p)) : onPick}
              className="max-h-[52vh]"
            />
          )}
        </QueryState>
      )}
      <TestPanel body={body} sampleRef={s.ref} hasOutput={hasOutput} problem={problem} />
    </Box>
  );
}
